import flow from 'lodash/fp/flow';
import isArray from 'lodash/isArray';

import type { Dataset } from '../types/dataset.types';
import type { Pipe } from '../types/pipes.types';
import type { System } from '../types/system.types';

// Fail-safe capitalization of a string
export const capitalize = (str: any) => {
  try {
    return str.charAt(0).toUpperCase() + str.slice(1);
  } catch {
    return str;
  }
};

export const getHumanizedDiskUsage = (bytes: number) => {
  if (!bytes || typeof bytes !== 'number') {
    return '';
  }

  const gb = bytes / 1e9;

  const decimalPlaces = gb >= 1 ? 1 : gb >= 0.01 ? 2 : 3;

  const gigaBytes = gb.toFixed(decimalPlaces);

  return `${gigaBytes} GB`;
};

export const getDeleteActionMessage = (
  selection: Pipe[] | System[] | Dataset[],
  title: string
): string => {
  if (selection.length === 0) {
    return '';
  }

  if (selection.length === 1) {
    return `Are you sure you want to delete "${selection[0]._id}" ${title}?`;
  }

  return `Are you sure you want to delete ${selection.length} selected ${title}s?`;
};

export const getConfirmBeforeFn =
  (confirm: (message?: string | undefined) => boolean = window.confirm) =>
  (text: string, onOkay?: () => void) => {
    const result = confirm(text);

    if (result) {
      onOkay?.();
    }
  };

export const confirmBefore = getConfirmBeforeFn();

const replaceProtocol = (url: string) => {
  if (url?.startsWith('http:')) {
    return url.replace('http:', 'ws:');
  } else if (url?.startsWith('https:')) {
    return url.replace('https:', 'wss:');
  }

  return url;
};

const replaceApiPath = (url: string) => (url?.indexOf('/api') > 0 ? url.replace('/api', '') : url);

export const getWebSocketUrlFromApiUrl = (url: string) =>
  flow(replaceProtocol, replaceApiPath)(url);

export type ShouldRenderRefreshButtonReturnValue = 'loading' | 'render' | 'not-render';

export const shouldRenderRefreshButton = (args: {
  isLiveUpdatesSupported: boolean;
  isLiveUpdatesEnabled?: boolean;
  failedToConnectToLiveUpdates?: boolean;
}): ShouldRenderRefreshButtonReturnValue => {
  if (!args || !args.isLiveUpdatesSupported || args.failedToConnectToLiveUpdates) {
    return 'render';
  }

  if (args.isLiveUpdatesSupported === true && args.isLiveUpdatesEnabled === true) {
    return 'not-render';
  }

  return 'render';
};

export const toObjectFn =
  (idField: string = '_id', initialValue = {}) =>
  <T>(inputArray: T[] = []) => {
    if (!inputArray || inputArray.length === 0) {
      return initialValue;
    }

    return inputArray.reduce<Record<string, T>>((accumulator, item) => {
      accumulator[(item as any)[idField]] = item;

      return accumulator;
    }, initialValue);
  };

export const toObject = toObjectFn();

export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return prototype === Object.prototype || prototype === null;
};

export const isDevEnv = import.meta.env.DEV;

export const removeFalsy = (values: Array<string | null | undefined>): string[] =>
  values.filter((value): value is string => typeof value === 'string' && value !== '');
